/**
 * offlineCache — a tiny IndexedDB key/value store used to keep the last
 * successful GET responses so the app can show data while offline.
 *
 * No external dependency: a ~one-store IndexedDB wrapper. Everything is
 * best-effort — any failure resolves to "no cache" rather than throwing, so it
 * can never break the online path.
 */
const DB_NAME = 'sellgo-offline';
const STORE = 'kv';
const DB_VERSION = 1;

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function run(mode, makeRequest) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = makeRequest(store);
        let result;
        if (request) request.onsuccess = () => { result = request.result; };
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

/** Get a value (or undefined). Never throws. Returns the stored wrapper. */
export async function cacheGet(key) {
  try { return await run('readonly', (s) => s.get(key)); }
  catch { return undefined; }
}

/** Store a value with a timestamp. Best-effort; never throws. */
export async function cacheSet(key, value) {
  try { await run('readwrite', (s) => s.put({ v: value, ts: Date.now() }, key)); }
  catch { /* ignore */ }
}

/**
 * Find the freshest cached value whose key matches a path prefix, ignoring the
 * query string. Lets an offline read fall back to the last response for the
 * same endpoint even if the params differ slightly. Returns the value or
 * undefined.
 */
export async function cacheLatestByPrefix(prefix) {
  try {
    const keys = (await run('readonly', (s) => s.getAllKeys())) || [];
    const matches = keys.filter((k) => k === prefix || (typeof k === 'string' && k.startsWith(`${prefix}?`)));
    let best;
    for (const k of matches) {
      const wrapped = await cacheGet(k);
      if (wrapped && (best === undefined || (wrapped.ts || 0) > (best.ts || 0))) best = wrapped;
    }
    return best ? best.v : undefined;
  } catch {
    return undefined;
  }
}

/** Delete a value. Best-effort. */
export async function cacheDel(key) {
  try { await run('readwrite', (s) => s.delete(key)); }
  catch { /* ignore */ }
}

/** List all keys. Never throws. */
export async function cacheKeys() {
  try { return (await run('readonly', (s) => s.getAllKeys())) || []; }
  catch { return []; }
}

/** Wipe everything (e.g. on logout). Best-effort. */
export async function cacheClear() {
  try { await run('readwrite', (s) => s.clear()); }
  catch { /* ignore */ }
}
